// Customer phone helpers — read/search/edit a customer's phone(s) regardless of
// which shape the record is in.
//
// TWO SHAPES EXIST, ON PURPOSE:
//   legacy  → a single string field. Customer records store it as `phone`;
//             saved quote/invoice documents store their copy as `customerPhone`.
//   new     → `phones: [{ number, label, isPrimary }]`, where `label` is free
//             text (a person's name, 'Office', 'Cell', …) and exactly one entry
//             is marked primary. The primary is the one number shown on
//             customer-facing documents.
//
// WHY BOTH: there are ~9,000 existing customers, and none of them have `phones`.
// Every function here reads the array when it is present and otherwise falls
// back to the legacy string, so no bulk migration is required — a customer
// upgrades to the array only when someone next edits and saves them, and any
// record never touched keeps working exactly as it does today.
//
// Because the fallback checks `customerPhone` before `phone`, these helpers
// also accept a saved DOCUMENT in place of a customer record.
//
// Pure module: no React, no Firestore, no imports, no side effects, and no
// mutation of the objects passed in.

// ── getPrimaryPhone(customer) ──────────────────────────────────────────────
// Returns the customer's primary phone NUMBER as a string, backward-compatible.
// - If customer.phones is a non-empty array: return the entry marked isPrimary;
//   if none is marked primary, return the FIRST entry's number.
// - Else fall back to the legacy customer.customerPhone (or customer.phone) string.
// - Else return ''.
export function getPrimaryPhone(customer) {
  if (!customer) return '';
  const phones = Array.isArray(customer.phones) ? customer.phones : null;
  if (phones && phones.length > 0) {
    const primary = phones.find(p => p && p.isPrimary && p.number);
    if (primary) return primary.number;
    const firstWithNumber = phones.find(p => p && p.number);
    return firstWithNumber ? firstWithNumber.number : '';
  }
  return customer.customerPhone || customer.phone || '';
}

// ── getPrimaryPhoneLabel(customer) ─────────────────────────────────────────
// Returns the label of the primary phone (e.g. 'Office', a name), or '' .
export function getPrimaryPhoneLabel(customer) {
  if (!customer) return '';
  const phones = Array.isArray(customer.phones) ? customer.phones : null;
  if (phones && phones.length > 0) {
    const primary = phones.find(p => p && p.isPrimary && p.number) || phones.find(p => p && p.number);
    return primary && primary.label ? primary.label : '';
  }
  return '';
}

// ── getAllPhoneNumbers(customer) ───────────────────────────────────────────
// Returns an array of all phone number strings for searching (new list +
// legacy field), de-duplicated, non-empty.
export function getAllPhoneNumbers(customer) {
  if (!customer) return [];
  const out = [];
  if (Array.isArray(customer.phones)) {
    customer.phones.forEach(p => { if (p && p.number) out.push(p.number); });
  }
  const legacy = customer.customerPhone || customer.phone;
  if (legacy) out.push(legacy);
  return [...new Set(out.filter(Boolean))];
}

// ── normalizePhones(customer) ──────────────────────────────────────────────
// Returns a phones array for EDITING: uses existing phones if present, else
// seeds a single entry from the legacy field (marked primary). Never mutates input.
export function normalizePhones(customer) {
  if (customer && Array.isArray(customer.phones) && customer.phones.length > 0) {
    // ensure exactly-one-primary invariant for editing: if none primary, mark first
    const hasPrimary = customer.phones.some(p => p && p.isPrimary);
    return customer.phones.map((p, i) => ({
      number: p?.number || '',
      label: p?.label || '',
      isPrimary: hasPrimary ? !!p?.isPrimary : i === 0,
    }));
  }
  const legacy = customer?.customerPhone || customer?.phone || '';
  return [{ number: legacy, label: '', isPrimary: true }];
}

// ── setPrimaryNumber(phones, number) ───────────────────────────────────────
// For editors that show ONE phone box (the Customers list form): returns a new
// phones array with the primary entry's number replaced by `number` (trimmed),
// keeping its label and every secondary number. A blank `number` removes the
// primary entry and promotes the first remaining one. Returns [] when nothing is
// left. `phones` is expected in normalizePhones shape. Never mutates input.
export function setPrimaryNumber(phones, number) {
  const value = (number || '').trim();
  const list = (phones || []).map(p => ({ ...p }));
  let idx = list.findIndex(p => p.isPrimary);
  if (idx === -1) idx = 0;
  if (list.length === 0) list.push({ number: '', label: '', isPrimary: true });
  list[idx] = { ...list[idx], number: value, isPrimary: true };
  const kept = list.filter(p => (p.number || '').trim());
  if (kept.length > 0 && !kept.some(p => p.isPrimary)) kept[0] = { ...kept[0], isPrimary: true };
  return kept;
}
